<template><div><h1 id="dimension" tabindex="-1"><a class="header-anchor" href="#dimension"><span>Dimension</span></a></h1>
<p>Dimension — Auswertedimension für das Reportingtool oder die Ereignisauswertung</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Eine Dimension definiert ein direktes oder auch abgeleitetes Attribut einer Adresse (resp. eines Artikels oder eines Auftrags). Beispielsweise liefert die Dimension Alter (für Adressen) das Alter in Jahren der Adresse (oder vielmehr der durch die Adresse beschriebenen Person).</p>
<p>Die Dimensionen können sowohl im Reportingtool wie auch in der Ereignisauswertung verwendet werden. Nicht jede Dimension macht in beiden Werkzeugen Sinn (beispielsweise sind alle Dimensionen für Artikel oder Aufträge nur im Reportingtool anwendbar).</p>
<p>Das Alter ist selbstverständlich abhängig vom Zeitpunkt der Auswertung (das Geburtsdatum bleibt immer gleich). Prinzipiell gilt das gleiche für alle Auswertedimensionen: Sie (d.h. ihre Werte) sind abhängig vom Zeitpunkt der Auswertung. Das im System nicht jeder Aspekt historisiert wird, ist es allerdings nicht bei allen Dimension möglich (oder teilweise sogar sinnvoll), die Abhängigkeit vom Auswertedatum zu implementieren.</p>
<p>Die Dimensionen in funtrade sind fest eingebaut und können nicht verändert werden.</p>
<p>Manche Dimensionen benötigen zusätzliche Parameter. Z.B. gibt es eine Dimension Ereignisumsatz , die für eine Adresse die Werte der Responseereignisse aufsummiert. Die Parameter dieser Dimension sind die Angabe von Umsatztyp und einem Datumsbereich. Die Parameter sind bei jeder Dimension einzeln beschrieben.</p>
<h3 id="die-implementierten-dimensionen" tabindex="-1"><a class="header-anchor" href="#die-implementierten-dimensionen"><span>Die implementierten Dimensionen</span></a></h3>
<p>In der untenstehenden Tabelle sind die implementierten Dimensionen beschrieben.</p>
<p><strong>Erklärung zu den einzelnen Spalten</strong></p>
<dl>
<dt>Dimension</dt>
<dd>Name der Dimension.</dd>
<dt>Beschreibung</dt>
<dd>Beschreibung der Dimension. Die Beschreibung geht davon aus, dass die Dimension für eine konkrete Adresse (resp. Artikel, resp. Auftrag) ausgewertet wird. Es wird erklärt, was das Resultat der Auswertung ist.</dd>
<dt>Reportingtool / Ereignisauswertung</dt>
<dd>Angabe, ob die Dimension im Reportingtool resp. in der Ereignisauswertung zur Verfügung steht.</dd>
<dt>Abh. vom Auswertedatum</dt>
<dd>Gibt an, ob die Dimension abhängig vom Auswertedatum evaluiert.</dd>
<dt>Parameter</dt>
<dd>Falls die Dimension parametrisierbar ist, werden die Parameter und die Syntax der Eingabe hier beschrieben.</dd>
<dt>Gruppierungen</dt>
<dd>Falls die Werte der Dimension durch den Benutzer parametrisierbar sind, wird das hier beschrieben.</dd>
</dl>
<p><strong>Tabelle 27. Auswerte-Dimensionen</strong></p>
<table>
<thead>
<tr>
<th>Dimension</th>
<th>Beschreibung</th>
<th>Reportingtool / Ereignis­auswertung</th>
<th>Abh. Auswertedatum</th>
<th>Parameter</th>
<th>Beispiel für Gruppierung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Quelle Adressen</td>
<td></td>
<td></td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Alter</td>
<td>Liefert das Alter der Person auf der Adresse in Jahren. Das ist die Differenz zw. dem Auswertejahr und dem Geburtsjahr auf der Adresse. Ist kein Geburtsdatum auf der Adresse vorhanden, wird die Adresse zu der Gruppe &quot;übrige&quot; gezählt.</td>
<td>ja / ja</td>
<td>ja</td>
<td>keine</td>
<td>18,25,35,50,70</td>
</tr>
<tr>
<td>Geschlecht</td>
<td>Liefert den Geschlechtscode der Adresse.</td>
<td>ja / ja</td>
<td>nein</td>
<td>keine</td>
<td>keine</td>
</tr>
<tr>
<td>Kanton</td>
<td>Liefert den Kantonscode der Adresse.</td>
<td>ja / ja</td>
<td>nein</td>
<td>keine</td>
<td>ZH,AR,GE</td>
</tr>
<tr>
<td>Sprache</td>
<td>Liefert den (ersten) Sprachcode der Adresse.</td>
<td>ja / ja</td>
<td>nein</td>
<td>keine</td>
<td>d,f,i</td>
</tr>
<tr>
<td>Ereignis / EP</td>
<td>Die Dimension verwendetet eine Liste von Ereignisnummern als Parameter. Für eine Adresse wird geprüft, welche der Ereignisse aus der Parameterliste auch tatsächlich bei der Adresse aufgetreten sind. Dabei spielt der Ereignistyp und Status keine Rolle. Nur Ereignisse mit Datum vor dem Auswertedatum werden geprüft. Zurückgegeben wird die Parameterliste, die auf die Ereignisse der Adresse verkürzt wurde.</td>
<td>ja / ja</td>
<td>ja</td>
<td>Eine Liste von Ereignisnummern (4 oder 6 stellig), z.B.: 103204,103205,1032</td>
<td>Ebenfalls eine Liste von Ereignisnummern. Im Reportingtool wird die Parameterliste als Gruppierung verwendet. Die Zuordnung zu den Gruppen geschieht strikt von links nach rechts. Eine Adresse wird in die erste zutreffende Gruppe gegeben.</td>
</tr>
<tr>
<td>Merkmal</td>
<td>Die Dimension verwendet eine Liste von Merkmalen und prüft bei einer Adresse, welche davon auch tatsächlich vorkommen. Zurückgegeben wird die verkürzte Parameterliste, die nur noch Merkmale der Adresse enthält.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Liste von Merkmalcodes, z.B. merk,ork,torg</td>
<td>Die Parameterliste wird auch für die Gruppierung verwendet. Eine Adresse wird in die erste zutreffende Gruppe (von links nach rechts) gegeben.</td>
</tr>
<tr>
<td>Personenstatus</td>
<td>Liefert den Personenstatus der Adresse als drei stellige Buchstaben-Kombination.</td>
<td>ja / ja</td>
<td>ja</td>
<td>keine</td>
<td>ZSK,ZS-,Z--,--- (oder jede andere Kombination aus - und Status)</td>
</tr>
<tr>
<td>Adressumsatz (Typ)</td>
<td>Liefert den Adressumsatz eines angegebenen Jahres und Typs als ganzzahliger Betrag in Systemwährung.</td>
<td>ja / ja</td>
<td>Ja, allerdings wird der Umsatz des gleichen Jahres wie das Auswertedatum vollständig zurückgegeben. Falls Interesse am Umsatz der Periode vom 1.1. bis um Auswertedatum besteht, soll die Dimension Ereignisumsatz verwendet werden.</td>
<td>Umsatztyp und Jahr, z.B. SPE,1998</td>
<td>Betragsstaffel, z.B.: 10,30,50,100,500</td>
</tr>
<tr>
<td>Akquisitions­code</td>
<td>Liefert den Akquisitionscode der zum Auswertezeitpunkt aktiven Zusage vom gegebenen Zusagentyp (oder ein leerer Wert, falls zum Auswertezeitpunkt keine aktive Zusage vom gegebenen Typ existierte).</td>
<td>ja / ja</td>
<td>ja</td>
<td>Zusagentyp, z.B. MIT</td>
<td>Ein oder mehrere Akquisitionscodes. Z.B. aq01,aq02</td>
</tr>
<tr>
<td>Ereignisumsatz (Typ)</td>
<td>Liefert den Ereignisumsatz im angebebenen Zeitraum und Typ als ganzzahliger Wert in Systemwährung. Der Zeitraum wird nach oben beschränkt durch das Auswertedatum.</td>
<td>ja / ja</td>
<td>ja</td>
<td>Umsatztyp, Datum von, Datum bis, z.B. SPE,01.01.1999,​31.03.1999 Datum von und Datum bis können auch leer gelassen werden, was dann keine Beschränkung nach unten oder oben bedeutet, z.B.: SPE,,,</td>
<td>Betragsstaffel, z.B.: 10,30,50,100,500</td>
</tr>
<tr>
<td>Personenstatus (Typ)</td>
<td>Liefert den Personenstatus der Person.</td>
<td>ja / ja</td>
<td>ja</td>
<td>Ein Kundenstatus, z.B. Z</td>
<td>Kundenstatus, z.B.: z,-</td>
</tr>
<tr>
<td>Kanal</td>
<td>Liefert den Kommunikationskanal.</td>
<td>nein / ja</td>
<td>nein</td>
<td>keine</td>
<td>Ein oder mehrere Kommunikationskanäle. Z.B. Post,E-Mail</td>
</tr>
<tr>
<td>Fremddb</td>
<td>Liefert die Fremddatenbank­nummer</td>
<td>nein / ja</td>
<td>nein</td>
<td>keine</td>
<td>Ein oder mehrere Fremddatenbank­nummern. Z.B. 10,15,21,35</td>
</tr>
<tr>
<td>PLZ</td>
<td>Erlaubt eine Selektion nach Postleitzahlen.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Liste von Postleitzahlen, Z.B. 8046,8047,8048 .</td>
<td></td>
</tr>
<tr>
<td>WEMF Agglomeration</td>
<td>Erlaubt eine Selektion nach WEMF-Agglomerationen.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Liste von WEMF Agglomerations-Nummern, Z.B. 230,261</td>
<td></td>
</tr>
<tr>
<td>WEMF Region</td>
<td>Erlaubt eine Selektion nach WEMF-Regionen.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Liste von WEMF Regionen-Nummern, Z.B. 2,3</td>
<td></td>
</tr>
<tr>
<td>WEMF Gebiet</td>
<td>Erlaubt eine Selektion nach WEMF-Gebieten.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Liste von WEMF Gebiets-Nummern, Z.B. 21,22</td>
<td></td>
</tr>
<tr>
<td>Quelle Artikel</td>
<td></td>
<td></td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Artikelgruppe</td>
<td>Liefert den Code der Artikelgruppe des Artikels.</td>
<td>ja / nein</td>
<td>nein</td>
<td>keine</td>
<td>spiele,karten,textil</td>
</tr>
<tr>
<td>Produkt­manager</td>
<td>Liefert den Code des Productmanagers des Artikels.</td>
<td>ja / nein</td>
<td>nein</td>
<td>keine</td>
<td>PM1,PM2,PM3</td>
</tr>
<tr>
<td>Artikelmerkmal</td>
<td>Die Dimension verwendet als Parameter eine Liste von Artikelmerkmalen. Die Liste wird verkürzt, sodass nur noch Merkmale des aktuellen Artikels vorkommen; diese wird zurückgegeben.</td>
<td>ja / nein</td>
<td>nein</td>
<td>Eine Liste von Artikelmerkmalen, z.B. ART,ARTE,Kart</td>
<td>Für die Gruppierung wird die Parameterliste verwendet. Die Zugehörigkeit eines Artikels in eine Gruppe wird von links nach rechts entschieden.</td>
</tr>
<tr>
<td>Quelle Auftrag</td>
<td></td>
<td></td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Auftragsarten</td>
<td>Liefert den Code der Auftragsart des Auftrags.</td>
<td>ja / nein</td>
<td>nein</td>
<td>keine</td>
<td>aa,zz</td>
</tr>
<tr>
<td>Auftragswert</td>
<td>Liefert den Auftragswert als Dezimalzahl in Landeswährung.</td>
<td>ja / nein</td>
<td>nein</td>
<td>keine</td>
<td>5.50,10.50,​20.99,50.00,​100,500</td>
</tr>
</tbody>
</table>
<p>Pfad: <code v-pre>Personen → Tabellen → RFM-Dimensionen</code></p>
<h2 id="datenfelder" tabindex="-1"><a class="header-anchor" href="#datenfelder"><span>Datenfelder</span></a></h2>
<dl>
<dt>GNr.</dt>
<dd>Nummer der Dimension (interner Identifikator).</dd>
<dt>Q</dt>
<dd>Quelle der Dimension. Die möglichen Quellen sind <span class="guilabel">A</span> für Adressen, <span class="guilabel">L</span> für Artikel und <span class="guilabel">W</span> für Aufträge.</dd>
<dt>Bezeichnung</dt>
<dd>Eine textuelle Beschreibung der Dimension.</dd>
<dt>Werte</dt>
<dd>Angabe, ob die Dimension diskrete Werte oder Zahlenwerte liefert.</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<p>N/A</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
<p><RouteLink to="/manual/customer/reportingtool.html" title="9. Reportingtool">Reportingtool</RouteLink> , <RouteLink to="/manual/customer/ereignisauswertung.html" title="6. Mailingauswertung">Ereignisauswertung</RouteLink></p>
</div></template>


